// src/pages/ClaudeAnalyzer.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ClaudeAnalysisForm from '../components/ClaudeAnalysisForm';
import { ArrowLeft } from 'lucide-react';

const ClaudeAnalyzer = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const quoteId = searchParams.get('quoteId');
  
  // Check authentication
  useEffect(() => {
    const storedAuth = localStorage.getItem('isAuthenticated');
    if (storedAuth === 'true') {
      setIsAuthenticated(true);
    } else {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);
  
  // Fetch quote data
  useEffect(() => {
    if (!isAuthenticated || !quoteId) return;
    
    const fetchQuote = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`/api/quotes/${quoteId}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch quote: ${response.statusText}`);
        }
        
        const data = await response.json();
        setQuote(data);
      } catch (error) {
        console.error('Error fetching quote:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuote();
  }, [isAuthenticated, quoteId]);
  
  // Handle analysis completion
  const handleAnalysisComplete = (result) => {
    console.log('Analysis complete:', result);
    // Wait for 2 seconds to show success message, then navigate back to framework tab
    setTimeout(() => {
      // Navigate back to framework tab
      navigate('/admin?tab=framework');
    }, 2000);
  };
  
  if (!isAuthenticated) {
    return null; // Will redirect to login via useEffect
  }
  
  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Claude AI Analysis</h1>
          <div>
            <Link 
              to="/admin?tab=framework" 
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Admin
            </Link>
          </div>
        </div>
        
        <div className="bg-white shadow-sm rounded-lg overflow-hidden mb-8">
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading quote data...</p>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
                <p className="font-bold">Error</p>
                <p>{error}</p>
              </div>
            </div>
          ) : quote ? (
            <div className="p-6">
              <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-200">
                <h2 className="text-lg font-medium text-gray-900 mb-2">Analyzing Claim</h2>
                <p className="font-medium text-gray-800 mb-2">"{quote.claim_text || quote.text}"</p>
                <div className="text-sm text-gray-600">
                  <p><span className="font-medium">Source:</span> {quote.claim_source || quote.source}</p>
                  <p><span className="font-medium">Date:</span> {quote.claim_date || quote.date}</p>
                  {quote.veracity_rating && (
                    <p><span className="font-medium">Rating:</span> {quote.veracity_rating}</p>
                  )}
                </div>
              </div>
              
              <ClaudeAnalysisForm 
                quote={quote} 
                onAnalysisComplete={handleAnalysisComplete} 
              />
            </div>
          ) : (
            <div className="p-6 text-center">
              <p className="text-gray-600">No quote found with ID {quoteId}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClaudeAnalyzer;